import type { DriveStep, Driver } from 'driver.js'
import i18n from 'modules/core/lib/i18n'
import { waitForElement, TOUR_ABOVE_OVERLAY_Z_INDEX } from './tourUtils'

function getPreviousMonthRoute(): string {
  const now = new Date()
  let year = now.getFullYear()
  let month = now.getMonth()
  if (month === 0) {
    month = 12
    year -= 1
  }
  return `/attendance/time-tracking/list/monthly/${year}/${month}/1`
}

export function getTaskRoute(taskKey: string): string {
  const routes: Record<string, () => string> = {
    filter_important: getPreviousMonthRoute,
    detect_alerts: getPreviousMonthRoute,
    bulk_approve: getPreviousMonthRoute,
  }
  return routes[taskKey]?.() ?? '/attendance/time-tracking'
}

export interface TourCallbacks {
  onShowConfetti: () => void
  onShowBookingModal: () => void
  onShowSuccessFlash: (message: string) => void
  onTaskCompleted: (taskKey: string) => void
  navigate: (path: string) => void
  skipReset: () => void
  resumeTour: (task: string, step: number) => void
}

declare global {
  interface DocumentEventMap {
    'dismissableLayer.focusOutside': CustomEvent<{ originalEvent: Event }>
    'dismissableLayer.pointerDownOutside': CustomEvent<{ originalEvent: Event }>
  }
}

let dismissShieldInstalled = false

function installDriverDismissShield(): void {
  if (dismissShieldInstalled) return
  dismissShieldInstalled = true
  const block = (e: CustomEvent<{ originalEvent: Event }>) => {
    const target = e.detail.originalEvent.target
    if (
      target instanceof Element &&
      target.closest('.driver-popover, .driver-overlay, .driver-active-element')
    ) {
      e.preventDefault()
    }
  }
  document.addEventListener('dismissableLayer.focusOutside', block, true)
  document.addEventListener('dismissableLayer.pointerDownOutside', block, true)
}

export async function prepareTaskStart(taskKey: string, startStep: number): Promise<boolean> {
  installDriverDismissShield()
  if (taskKey === 'detect_alerts' && startStep === 0) {
    const firstRow = document.querySelector<HTMLElement>('tbody tr')
    const detailsBtn = firstRow?.querySelector<HTMLElement>('td:nth-last-child(2) button')
    if (!detailsBtn) return false
    detailsBtn.click()
    const dialog = await waitForElement('[role="dialog"][data-state="open"]', 5000).catch(
      () => null
    )
    if (!dialog) return false
    await new Promise<void>((resolve) => setTimeout(resolve, 400))
    tagOpenTimesheetButton()
  }
  return true
}

export function preTagElements(taskKey: string): void {
  if (taskKey === 'filter_important') {
    const sidebarLink = document.querySelector<HTMLElement>(
      'nav a[href*="/attendance/time-tracking"]'
    )
    if (sidebarLink) sidebarLink.setAttribute('data-tour', 'sidebar-time-tracking')

    const alertsOpt = findAlertsOption()
    if (alertsOpt) alertsOpt.setAttribute('data-tour', 'alerts-filter')
  }

  if (taskKey === 'detect_alerts') {
    const chevron = document.querySelector<HTMLElement>(
      '[data-intercom-target="attendance-row-toggle"]'
    )
    if (chevron) chevron.setAttribute('data-tour', 'day-row-chevron')
  }

  if (taskKey === 'bulk_approve') {
    const pendingLabel = findPresetLabel(['pendiente', 'pending'])
    if (pendingLabel) pendingLabel.setAttribute('data-tour', 'pending-preset')

    const checkbox = document.querySelector<HTMLElement>(
      'thead [role="checkbox"], thead input[type="checkbox"]'
    )
    if (checkbox) checkbox.setAttribute('data-tour', 'select-all-checkbox')
  }
}

function findFiltersButton(): HTMLButtonElement | null {
  const allButtons = Array.from(document.querySelectorAll<HTMLButtonElement>('button'))
  for (const btn of allButtons) {
    const label = btn.getAttribute('aria-label')?.toLowerCase() ?? ''
    const text = btn.textContent?.trim().toLowerCase() ?? ''
    if (label.includes('filter') || text === 'filters' || text === 'filtros' || text === 'filter') {
      return btn
    }
  }
  return null
}

function findApplyButton(): HTMLButtonElement | null {
  const buttons = Array.from(document.querySelectorAll<HTMLButtonElement>('button'))
  for (const btn of buttons) {
    const text = btn.textContent?.trim().toLowerCase() ?? ''
    if (/^(apply filters?|aplicar filtros?)$/i.test(text)) return btn
  }
  return null
}

function bumpRadixPopoverZIndex(): void {
  const wrapper = document.querySelector<HTMLElement>('[data-radix-popper-content-wrapper]')
  if (wrapper) {
    wrapper.style.zIndex = TOUR_ABOVE_OVERLAY_Z_INDEX
  }
}

function restoreRadixPopoverZIndex(): void {
  const wrapper = document.querySelector<HTMLElement>('[data-radix-popper-content-wrapper]')
  if (wrapper) {
    wrapper.style.zIndex = ''
  }
}

function tagOpenTimesheetButton(): void {
  const dialog = document.querySelector<HTMLElement>('[role="dialog"][data-state="open"]')
  if (!dialog) return
  const buttons = Array.from(dialog.querySelectorAll<HTMLButtonElement>('button'))
  const openTimesheetBtn = buttons.find((btn) => {
    const label = btn.getAttribute('aria-label')?.toLowerCase() ?? ''
    const text = btn.textContent?.trim().toLowerCase() ?? ''
    const matches =
      label.includes('hoja') ||
      label.includes('timesheet') ||
      text.includes('hoja') ||
      text.includes('timesheet')
    return matches && btn.offsetParent !== null
  })
  if (openTimesheetBtn) {
    openTimesheetBtn.setAttribute('data-tour', 'open-timesheet-btn')
  }
}

function findAlertsOption(): HTMLElement | null {
  const alertsText = i18n.t('time_tracking.table_collection.filters.alerts')
  const popover = document.querySelector<HTMLElement>('[data-radix-popper-content-wrapper]')
  const scope = popover ?? document
  const candidates = Array.from(
    scope.querySelectorAll<HTMLElement>('button, label, [role="checkbox"], [role="option"]')
  )
  for (const el of candidates) {
    const text = el.textContent?.trim() ?? ''
    if (text === alertsText || text.includes(alertsText)) return el
  }
  return null
}

function findBulkActionButton(match: RegExp): HTMLButtonElement | null {
  const buttons = Array.from(document.querySelectorAll<HTMLButtonElement>('button'))
  for (const btn of buttons) {
    if (btn.offsetParent === null) continue
    if (btn.closest('[role="dialog"]')) continue
    const label = btn.getAttribute('aria-label') ?? ''
    const text = btn.textContent ?? ''
    if (match.test(label) || match.test(text)) return btn
  }
  return null
}

const findBulkApproveButton = () => findBulkActionButton(/^\s*(aprobar|approve)\s*$/i)
const findBulkCompensateButton = () => findBulkActionButton(/^\s*(compensar|compensate)\s*$/i)

function findVisibleConfirmButton(): HTMLButtonElement | null {
  const isConfirm = (s: string) => /^\s*(confirmar|confirm)\s*$/i.test(s)
  const buttons = Array.from(document.querySelectorAll<HTMLButtonElement>('button[type="submit"]'))
  for (const btn of buttons) {
    if (btn.offsetParent === null) continue
    if (btn.closest('.driver-popover')) continue
    const text = btn.textContent ?? ''
    const label = btn.getAttribute('aria-label') ?? ''
    if (isConfirm(text) || isConfirm(label)) return btn
  }
  return null
}

function findVisibleFinishButton(): HTMLButtonElement | null {
  const isFinish = (s: string) => /^\s*(finalizada|finalizar|done|finish(ed)?)\s*$/i.test(s)
  const buttons = Array.from(document.querySelectorAll<HTMLButtonElement>('button'))
  for (const btn of buttons) {
    if (btn.offsetParent === null) continue
    if (btn.closest('.driver-popover')) continue
    const text = btn.textContent ?? ''
    const label = btn.getAttribute('aria-label') ?? ''
    if (isFinish(text) || isFinish(label)) return btn
  }
  return null
}

function pollFor<T>(check: () => T | null | undefined, timeoutMs: number): Promise<T | null> {
  return new Promise((resolve) => {
    let settled = false
    const finish = (value: T | null) => {
      if (settled) return
      settled = true
      observer.disconnect()
      clearTimeout(timer)
      clearInterval(safety)
      resolve(value)
    }
    const tryCheck = () => {
      const v = check()
      if (v) finish(v)
    }
    const observer = new MutationObserver(tryCheck)
    observer.observe(document.body, { childList: true, subtree: true, attributes: true })
    const timer = setTimeout(() => finish(null), timeoutMs)
    const safety = setInterval(tryCheck, 500)
    tryCheck()
  })
}

function tagAddShiftButton(): HTMLElement | null {
  const inners = Array.from(
    document.querySelectorAll<HTMLElement>(
      '[data-intercom-target="attendance-row-add-shift-button"]'
    )
  )
  const inner = inners.find((b) => b.offsetParent !== null)
  if (!inner) return null
  // The visible Añadir button is wrapped by a Radix popover trigger button
  // (aria-haspopup="dialog"). Highlight the outer trigger when present so the
  // whole clickable area is framed; fall back to the inner visual button.
  const outer = inner.parentElement
  const target =
    outer instanceof HTMLButtonElement && outer.getAttribute('aria-haspopup') === 'dialog'
      ? outer
      : inner
  target.setAttribute('data-tour', 'add-shift-btn')
  return target
}

function findPresetLabel(keywords: string[]): HTMLElement | null {
  const labels = Array.from(document.querySelectorAll<HTMLElement>('label, button, [role="tab"]'))
  for (const el of labels) {
    const text = el.textContent?.trim().toLowerCase() ?? ''
    if (keywords.some((kw) => text.startsWith(kw))) return el
  }
  return null
}

type TaskStepsFactory = (driverInstance: Driver, callbacks: TourCallbacks) => DriveStep[]

const filterImportantSteps: TaskStepsFactory = (driverObj, callbacks) => [
  {
    element: 'nav a[href*="/attendance/time-tracking"]',
    popover: {
      title: i18n.t('growth.get_started.demo_tour.step_sidebar_title'),
      description: i18n.t('growth.get_started.demo_tour.step_sidebar_description'),
      side: 'right',
      align: 'center',
      onNextClick: () => {
        // Already on the time tracking page: advance in place, since navigating
        // to the same route is a no-op and would never trigger the resume effect.
        if (window.location.pathname.startsWith('/attendance/time-tracking/list')) {
          driverObj.moveNext()
          return
        }
        callbacks.skipReset()
        driverObj.destroy()
        try {
          sessionStorage.setItem(
            'demo-tour-resume',
            JSON.stringify({ task: 'filter_important', step: 1 })
          )
        } catch {}
        callbacks.navigate(getTaskRoute('filter_important'))
      },
    },
    onHighlightStarted: () => {
      const link = document.querySelector<HTMLElement>('nav a[href*="/attendance/time-tracking"]')
      if (link) {
        link.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    },
  },
  {
    element: 'tbody',
    popover: {
      title: i18n.t('growth.get_started.demo_tour.step_table_title'),
      description: i18n.t('growth.get_started.demo_tour.step_table_description'),
      side: 'top',
      align: 'center',
      onNextClick: () => {
        const btn = findFiltersButton()
        if (btn) {
          btn.setAttribute('data-tour', 'filters-button')
        }
        driverObj.moveNext()
      },
    },
    onHighlightStarted: () => {
      const tbody = document.querySelector('tbody')
      if (tbody) tbody.scrollIntoView({ behavior: 'smooth', block: 'center' })
    },
  },
  {
    element: '[data-tour="filters-button"]',
    popover: {
      title: i18n.t('growth.get_started.demo_tour.step_filters_title'),
      onNextClick: () => {
        const btn = findFiltersButton()
        if (btn) btn.click()
        setTimeout(() => {
          const alertsBtn = findAlertsOption()
          if (alertsBtn) {
            alertsBtn.setAttribute('data-tour', 'alerts-filter')
          }
          bumpRadixPopoverZIndex()
          setTimeout(() => driverObj.moveNext(), 300)
        }, 500)
      },
    },
    onHighlightStarted: () => {
      const btn = findFiltersButton()
      if (btn) {
        btn.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    },
  },
  {
    element: '[data-tour="alerts-filter"]',
    popover: {
      title: i18n.t('growth.get_started.demo_tour.step_alerts_title'),
      description: i18n.t('growth.get_started.demo_tour.step_alerts_description'),
      onNextClick: () => {
        const alertsBtn = findAlertsOption()
        if (alertsBtn) alertsBtn.click()
        void waitForElement('#select-all', 2000)
          .then(() => {
            setTimeout(() => {
              const selectAll = document.querySelector<HTMLElement>('#select-all')
              if (selectAll) selectAll.click()

              setTimeout(() => {
                const applyBtn = findApplyButton()
                if (applyBtn) applyBtn.click()
                restoreRadixPopoverZIndex()
                setTimeout(() => driverObj.moveNext(), 500)
              }, 300)
            }, 300)
          })
          .catch(() => driverObj.moveNext())
      },
    },
    onHighlightStarted: () => {
      bumpRadixPopoverZIndex()
    },
  },
  {
    element: 'tbody',
    popover: {
      title: i18n.t('growth.get_started.demo_tour.step_done_title'),
      description: i18n.t('growth.get_started.demo_tour.step_done_description'),
      onNextClick: () => {
        callbacks.onTaskCompleted('filter_important')
      },
    },
    onHighlightStarted: () => {
      restoreRadixPopoverZIndex()
      const tbody = document.querySelector('tbody')
      if (tbody) tbody.scrollIntoView({ behavior: 'smooth', block: 'center' })
    },
  },
]

function findAlertDayRow(): HTMLElement | null {
  const rows = Array.from(document.querySelectorAll<HTMLElement>('tr'))
  for (const row of rows) {
    const alert = row.querySelector(
      '[data-testid*="alert"], [class*="alert"], svg[class*="warning"]'
    )
    if (alert) return row
  }
  const chevrons = Array.from(
    document.querySelectorAll<HTMLElement>('[data-intercom-target="attendance-row-toggle"]')
  )
  return chevrons[0]?.closest('tr') ?? null
}

const detectAlertsSteps: TaskStepsFactory = (driverObj, callbacks) => [
  {
    element: '[role="dialog"][data-state="open"]',
    popover: {
      title: i18n.t('growth.get_started.demo_tour.step_sidepanel_title'),
      description: i18n.t('growth.get_started.demo_tour.step_sidepanel_description'),
      side: 'left',
    },
  },
  {
    element: '[data-tour="open-timesheet-btn"]',
    popover: {
      title: i18n.t('growth.get_started.demo_tour.step_open_timesheet_title'),
      side: 'left',
      onNextClick: () => {
        const openBtn = document.querySelector<HTMLElement>('[data-tour="open-timesheet-btn"]')
        if (!openBtn) {
          driverObj.destroy()
          return
        }
        try {
          sessionStorage.setItem(
            'demo-tour-resume',
            JSON.stringify({ task: 'detect_alerts', step: 2 })
          )
        } catch {}
        callbacks.skipReset()
        openBtn.click()
        driverObj.destroy()
      },
    },
  },
  {
    element: '[data-tour="day-row-chevron"]',
    popover: {
      title: i18n.t('growth.get_started.demo_tour.step_expand_day_title'),
      description: i18n.t('growth.get_started.demo_tour.step_expand_day_description'),
      onNextClick: () => {
        const chevron = document.querySelector<HTMLElement>('[data-tour="day-row-chevron"]')
        if (chevron) chevron.click()
        void waitForElement('[data-intercom-target="attendance-row-add-shift-button"]', 2000)
          .then(() => {
            setTimeout(() => {
              tagAddShiftButton()
              driverObj.moveNext()
            }, 300)
          })
          .catch(() => driverObj.moveNext())
      },
    },
    onHighlightStarted: () => {
      const alertRow = findAlertDayRow()
      const chevron = alertRow?.querySelector<HTMLElement>(
        '[data-intercom-target="attendance-row-toggle"]'
      )
      if (chevron) {
        chevron.setAttribute('data-tour', 'day-row-chevron')
        chevron.scrollIntoView({ behavior: 'smooth', block: 'center' })
      } else {
        const fallback = document.querySelector<HTMLElement>(
          '[data-intercom-target="attendance-row-toggle"]'
        )
        if (fallback) {
          fallback.setAttribute('data-tour', 'day-row-chevron')
          fallback.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }
    },
  },
  {
    element: '[data-tour="add-shift-btn"]',
    popover: {
      title: i18n.t('growth.get_started.demo_tour.step_shift_title'),
      description: i18n.t('growth.get_started.demo_tour.step_shift_description'),
      onNextClick: () => {
        callbacks.onTaskCompleted('detect_alerts')
        callbacks.onShowSuccessFlash(i18n.t('growth.get_started.demo_tour.flash_alert_fixed'))
        callbacks.onShowConfetti()
        setTimeout(() => {
          callbacks.onShowBookingModal()
        }, 2000)
      },
    },
    onHighlightStarted: () => {
      const btn = tagAddShiftButton()
      if (btn) btn.scrollIntoView({ behavior: 'smooth', block: 'center' })
    },
  },
]

const bulkApproveSteps: TaskStepsFactory = (driverObj, callbacks) => [
  {
    element: '[data-tour="pending-preset"]',
    popover: {
      title: i18n.t('growth.get_started.demo_tour.step_pending_title'),
      description: i18n.t('growth.get_started.demo_tour.step_pending_description'),
      onNextClick: () => {
        setTimeout(() => driverObj.moveNext(), 800)
      },
    },
    onHighlightStarted: () => {
      const pending = findPresetLabel(['pendiente', 'pending'])
      if (pending) {
        pending.click()
        pending.setAttribute('data-tour', 'pending-preset')
        pending.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    },
  },
  {
    element: '[data-tour="select-all-checkbox"]',
    popover: {
      title: i18n.t('growth.get_started.demo_tour.step_select_all_title'),
      description: i18n.t('growth.get_started.demo_tour.step_select_all_description'),
      onNextClick: () => {
        const checkbox = document.querySelector<HTMLElement>('[data-tour="select-all-checkbox"]')
        if (checkbox) checkbox.click()
        void pollFor(findBulkApproveButton, 3000).then((btn) => {
          if (!btn) {
            driverObj.destroy()
            return
          }
          btn.setAttribute('data-tour', 'bulk-approve-btn')
          const bar = btn.closest('[class*="fixed"][class*="bottom-"]')
          if (bar instanceof HTMLElement) bar.style.zIndex = TOUR_ABOVE_OVERLAY_Z_INDEX
          driverObj.moveNext()
        })
      },
    },
    onHighlightStarted: () => {
      const checkbox = document.querySelector<HTMLElement>(
        'thead [role="checkbox"], thead input[type="checkbox"]'
      )
      if (checkbox) {
        checkbox.setAttribute('data-tour', 'select-all-checkbox')
        checkbox.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    },
  },
  {
    element: '[data-tour="bulk-approve-btn"]',
    popover: {
      title: i18n.t('growth.get_started.demo_tour.step_bulk_approve_title'),
      description: i18n.t('growth.get_started.demo_tour.step_bulk_approve_description'),
      side: 'top',
      onNextClick: () => {
        const approveBtn =
          document.querySelector<HTMLButtonElement>('[data-tour="bulk-approve-btn"]') ??
          findBulkApproveButton()
        if (!approveBtn) {
          driverObj.destroy()
          return
        }
        approveBtn.click()

        setTimeout(() => {
          callbacks.onShowSuccessFlash(i18n.t('growth.get_started.demo_tour.flash_approved'))
          callbacks.onShowConfetti()
        }, 1500)

        setTimeout(() => {
          const approvedLabel = findPresetLabel(['aprobado', 'approved'])
          if (approvedLabel) approvedLabel.click()
          setTimeout(() => {
            const checkbox = document.querySelector<HTMLElement>(
              'thead [role="checkbox"], thead input[type="checkbox"]'
            )
            if (checkbox) checkbox.click()

            void pollFor(findBulkCompensateButton, 5000).then((btn) => {
              if (!btn) {
                driverObj.destroy()
                return
              }
              btn.setAttribute('data-tour', 'compensate-btn')
              const bar = btn.closest('[class*="fixed"][class*="bottom-"]')
              if (bar instanceof HTMLElement) bar.style.zIndex = TOUR_ABOVE_OVERLAY_Z_INDEX
              setTimeout(() => driverObj.moveNext(), 200)
            })
          }, 1000)
        }, 3000)
      },
    },
    onHighlightStarted: () => {
      const bar = document.querySelector<HTMLElement>('.fixed.bottom-2.z-50')
      if (bar) {
        bar.style.zIndex = TOUR_ABOVE_OVERLAY_Z_INDEX
      }
    },
  },
  {
    element: '[data-tour="compensate-btn"]',
    popover: {
      title: i18n.t('growth.get_started.demo_tour.step_payroll_sidebar_title'),
      description: i18n.t('growth.get_started.demo_tour.step_payroll_sidebar_description'),
      side: 'top',
      onNextClick: () => {
        const compensateBtn =
          document.querySelector<HTMLButtonElement>('[data-tour="compensate-btn"]') ??
          findBulkCompensateButton()
        if (compensateBtn) compensateBtn.click()

        void pollFor(findVisibleConfirmButton, 3000).then((confirmBtn) => {
          if (!confirmBtn) {
            driverObj.destroy()
            return
          }
          confirmBtn.click()

          void pollFor(findVisibleFinishButton, 30000).then((finishBtn) => {
            if (!finishBtn) {
              driverObj.destroy()
              return
            }
            finishBtn.click()
            callbacks.onTaskCompleted('bulk_approve')

            setTimeout(() => {
              callbacks.onShowSuccessFlash(i18n.t('growth.get_started.demo_tour.flash_compensated'))
              callbacks.onShowConfetti()
            }, 500)

            setTimeout(() => {
              callbacks.onShowBookingModal()
            }, 2500)
          })
        })
      },
    },
    onHighlightStarted: () => {
      const btn =
        document.querySelector<HTMLElement>('[data-tour="compensate-btn"]') ??
        findBulkCompensateButton()
      if (btn) {
        btn.setAttribute('data-tour', 'compensate-btn')
        const bar = btn.closest('[class*="fixed"][class*="bottom-"]')
        if (bar instanceof HTMLElement) bar.style.zIndex = TOUR_ABOVE_OVERLAY_Z_INDEX
        btn.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    },
  },
]

export const TASK_STEP_COUNTS: Record<string, number> = {
  filter_important: 5,
  detect_alerts: 4,
  bulk_approve: 4,
}

export function getReadySelector(
  taskKey: string,
  step: number
): { selector: string; timeout: number } {
  if (taskKey === 'filter_important' && step === 0) {
    return { selector: 'nav a[href*="/attendance/time-tracking"]', timeout: 3000 }
  }
  if (taskKey === 'detect_alerts' && step >= 2) {
    return { selector: '[data-intercom-target="attendance-row-toggle"]', timeout: 10000 }
  }
  return { selector: 'tbody', timeout: step === 0 ? 5000 : 10000 }
}

export function getTaskSteps(
  taskKey: string,
  driverInstance: Driver,
  callbacks?: TourCallbacks
): DriveStep[] {
  const cbs: TourCallbacks = callbacks ?? {
    onShowConfetti: () => {},
    onShowBookingModal: () => {},
    onShowSuccessFlash: () => {},
    onTaskCompleted: () => {},
    navigate: () => {},
    skipReset: () => {},
    resumeTour: () => {},
  }
  switch (taskKey) {
    case 'filter_important':
      return filterImportantSteps(driverInstance, cbs)
    case 'detect_alerts':
      return detectAlertsSteps(driverInstance, cbs)
    case 'bulk_approve':
      return bulkApproveSteps(driverInstance, cbs)
    default:
      return []
  }
}
